import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  cashVouchersApi,
  METHOD_LABEL,
  RECEIVER_LABEL,
  suggestDebitAccountByPrType,
  type CreateCashVoucherInput,
  type ReceiverType,
  type VoucherMethod,
} from '../lib/api/cash-vouchers';
import { chartOfAccountsApi, type Account } from '../lib/api/chart-of-accounts';
import { companyApi, type Bank } from '../lib/api/company';
import { cashEntriesApi, type CashSummary } from '../lib/api/cash-entries';
import type { PaymentRequest } from '../lib/api/payments';
import { formatVND } from '../lib/format';

interface Props {
  open: boolean;
  paymentRequest: PaymentRequest | null;
  onClose: () => void;
  onSuccess: () => Promise<void> | void;
}

/** Convert 1 CompanyBank thành label hiển thị (VD "VietinBank · 0121xxx8899"). */
function formatBankOption(b: Bank): string {
  return `${b.bankName} · ${b.account}${b.branch ? ` · ${b.branch}` : ''}`;
}

function fmtShortVND(n: number): string {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(2).replace(/\.?0+$/, '')}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  return n.toLocaleString('vi-VN');
}

export function CashVoucherFormModal({ open, paymentRequest, onClose, onSuccess }: Props) {
  const { user } = useAuth();
  const [method, setMethod] = useState<VoucherMethod>('chuyen_khoan');
  const [voucherDate, setVoucherDate] = useState('');
  const [documentDate, setDocumentDate] = useState('');
  const [actualAmount, setActualAmount] = useState(0);
  const [amountInput, setAmountInput] = useState('');
  const [receiverType, setReceiverType] = useState<ReceiverType>('supplier');
  const [receiverName, setReceiverName] = useState('');
  const [receiverIdNumber, setReceiverIdNumber] = useState('');
  const [note, setNote] = useState('');
  const [bankAccountUsed, setBankAccountUsed] = useState('');
  const [uncNumber, setUncNumber] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [companyBanks, setCompanyBanks] = useState<Bank[]>([]);
  const [debitAccount, setDebitAccount] = useState('');
  const [debitAccounts, setDebitAccounts] = useState<Account[]>([]);
  /** Cho thuong_nong: 'all' = chi 1 lần tổng tiền cho đại diện; hoặc employeeId cụ thể; hoặc 'custom' = nhập tên tự do. */
  const [bonusPick, setBonusPick] = useState<'all' | 'custom' | string>('all');
  const [cashSummary, setCashSummary] = useState<CashSummary | null>(null);

  const bankOptions = useMemo(() => companyBanks.map(formatBankOption), [companyBanks]);
  const selectedBank = useMemo(
    () => companyBanks.find((b) => formatBankOption(b) === bankAccountUsed) ?? null,
    [companyBanks, bankAccountUsed],
  );
  const availableBalance = method === 'chuyen_khoan' ? selectedBank?.balance : method === 'tien_mat_vnd' ? cashSummary?.closingBalance : undefined;
  const isOverBalance = availableBalance !== undefined && actualAmount > availableBalance;

  const debitAccountLabel = useMemo(() => {
    const acc = debitAccounts.find((a) => a.code === debitAccount);
    return acc ? acc.name : '';
  }, [debitAccount, debitAccounts]);

  // Load company banks 1 lần khi mở modal — không hardcode nữa.
  useEffect(() => {
    if (!open) return;
    companyApi.get()
      .then((c) => setCompanyBanks(c?.banks ?? []))
      .catch(() => setCompanyBanks([]));
  }, [open]);

  // Load COA cho picker TK Nợ — filter active + leaf, ưu tiên type asset/expense/liability.
  useEffect(() => {
    if (!open) return;
    chartOfAccountsApi.listFlat({ activeOnly: 'true' })
      .then((r) => {
        const eligible = r.items.filter((a) =>
          a.isLeaf && (a.type === 'asset' || a.type === 'expense' || a.type === 'liability'),
        );
        setDebitAccounts(eligible);
      })
      .catch(() => setDebitAccounts([]));
  }, [open]);

  // Auto-set default TK Nợ khi PR đổi hoặc COA vừa load xong.
  useEffect(() => {
    if (!open || !paymentRequest) return;
    const suggested = suggestDebitAccountByPrType(paymentRequest.type);
    // Chỉ set nếu suggestion tồn tại trong COA đã load. Nếu chưa load → set string, sẽ check sau.
    if (debitAccounts.length === 0 || debitAccounts.some((a) => a.code === suggested)) {
      setDebitAccount(suggested);
    } else {
      // fallback về TK đầu tiên loại expense nếu có
      const fallback = debitAccounts.find((a) => a.type === 'expense') ?? debitAccounts[0];
      setDebitAccount(fallback?.code ?? suggested);
    }
  }, [open, paymentRequest, debitAccounts]);

  useEffect(() => {
    if (!open || !paymentRequest) return;
    const today = new Date().toISOString().slice(0, 10);
    setMethod('chuyen_khoan');
    setVoucherDate(today);
    setDocumentDate(today);
    setActualAmount(paymentRequest.amount);
    setAmountInput(paymentRequest.amount.toLocaleString('vi-VN'));
    setReceiverType(paymentRequest.type === 'thuong_nong' || paymentRequest.type === 'hoan_ung' ? 'employee' : 'supplier');
    // Default receiver name theo type
    if (paymentRequest.type === 'thuong_nong' && paymentRequest.recipients?.length) {
      // Mặc định 'all' → tên là joined list
      setBonusPick('all');
      const names = paymentRequest.recipients.map((r) => r.employeeName).join(', ');
      setReceiverName(`Tất cả (${paymentRequest.recipients.length} NV): ${names}`.slice(0, 200));
    } else {
      setBonusPick('all');
      setReceiverName(paymentRequest.supplier || paymentRequest.requesterName || '');
    }
    setReceiverIdNumber('');
    setNote(`Chi theo ĐNC ${paymentRequest.code} · ${paymentRequest.reason.slice(0, 80)}`);
    setUncNumber('');
    setError(null);
  }, [open, paymentRequest]);

  /** Handle chọn recipient từ dropdown (chỉ thuong_nong). */
  const handleBonusPick = (value: string) => {
    setBonusPick(value);
    if (!paymentRequest?.recipients) return;
    if (value === 'all') {
      const names = paymentRequest.recipients.map((r) => r.employeeName).join(', ');
      setReceiverName(`Tất cả (${paymentRequest.recipients.length} NV): ${names}`.slice(0, 200));
      setActualAmount(paymentRequest.amount);
      setAmountInput(paymentRequest.amount.toLocaleString('vi-VN'));
    } else if (value === 'custom') {
      setReceiverName('');
    } else {
      // employeeId
      const r = paymentRequest.recipients.find((x) => x.employeeId === value);
      if (r) {
        setReceiverName(r.employeeName);
        setActualAmount(r.amount);
        setAmountInput(r.amount.toLocaleString('vi-VN'));
      }
    }
  };

  // Khi banks load xong hoặc user chọn method khác → chọn default bank.
  useEffect(() => {
    if (!open || !paymentRequest) return;
    if (bankOptions.length === 0) { setBankAccountUsed(''); return; }
    // Ưu tiên bank được set trên PR nếu match được, fallback default bank (isDefault=true), fallback first.
    const prefer = paymentRequest.bankAccount && bankOptions.find((o) => o.includes(paymentRequest.bankAccount!));
    const defaultBank = companyBanks.find((b) => b.isDefault);
    setBankAccountUsed(prefer ?? (defaultBank ? formatBankOption(defaultBank) : bankOptions[0]));
  }, [open, paymentRequest, bankOptions, companyBanks]);

  // Tiền mặt cũng phải đối chiếu số dư quỹ 1111 trước khi lập phiếu chi.
  useEffect(() => {
    if (!open || method !== 'tien_mat_vnd') return;
    cashEntriesApi.list({ method: 'cash_1111' })
      .then(({ summary }) => setCashSummary(summary))
      .catch(() => setCashSummary(null));
  }, [open, method]);

  if (!open || !paymentRequest) return null;

  const isBank = method === 'chuyen_khoan';
  const creditAccount = method === 'tien_mat_vnd' ? '1111' : method === 'chuyen_khoan' ? '1121' : '1112';

  const handleAmount = (raw: string) => {
    const digits = raw.replace(/\D/g, '');
    setAmountInput(digits ? Number(digits).toLocaleString('vi-VN') : '');
    setActualAmount(digits ? Number(digits) : 0);
  };

  const handleSubmit = async () => {
    setError(null);
    if (actualAmount < 1000) return setError('Số tiền tối thiểu 1.000đ');
    if (actualAmount > paymentRequest.amount) {
      return setError(
        `Số tiền chi thực (${formatVND(actualAmount)}) vượt số duyệt (${formatVND(paymentRequest.amount)}). ` +
        `Cần KTT duyệt lại ĐNC với mức mới trước khi chi.`,
      );
    }
    if (!receiverName.trim()) return setError('Nhập tên người nhận');
    if (isBank && !bankAccountUsed) {
      return setError(
        companyBanks.length === 0
          ? 'Công ty chưa có TK ngân hàng nào. Vào Cài đặt → Ngân hàng để thêm.'
          : 'Chọn tài khoản NH nguồn.',
      );
    }
    if (isBank && selectedBank && actualAmount > selectedBank.balance) {
      return setError(
        `Vượt số dư khả dụng của ${selectedBank.bankName}. ` +
        `Số dư: ${formatVND(selectedBank.balance)} · Yêu cầu chi: ${formatVND(actualAmount)}. ` +
        `Chọn TK khác hoặc giảm số tiền.`,
      );
    }
    if (method === 'tien_mat_vnd' && cashSummary && actualAmount > cashSummary.closingBalance) {
      return setError(`Vượt số dư quỹ tiền mặt khả dụng (${formatVND(cashSummary.closingBalance)}).`);
    }
    if (new Date(voucherDate) < new Date(documentDate)) {
      return setError('Ngày lập phiếu không được nhỏ hơn ngày chứng từ.');
    }
    if (!debitAccount.trim()) return setError('Chọn TK Nợ để hạch toán.');

    const payload: CreateCashVoucherInput = {
      method,
      voucherDate,
      documentDate,
      actualAmount,
      receiverType,
      receiverName: receiverName.trim(),
      receiverIdNumber: receiverIdNumber.trim() || undefined,
      note: note.trim() || undefined,
      debitAccount: debitAccount.trim(),
      bankAccountUsed: isBank ? bankAccountUsed : undefined,
      uncNumber: isBank ? (uncNumber.trim() || undefined) : undefined,
    };

    setSaving(true);
    try {
      await cashVouchersApi.create(paymentRequest.id, payload);
      await onSuccess();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cvm-backdrop" onClick={onClose}>
      <div className="cvm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cvm-head">
          <div>
            <div className="cvm-crumb">KẾ TOÁN › THANH TOÁN › LẬP PHIẾU CHI</div>
            <h2>Lập phiếu chi</h2>
            <p>Từ đề nghị <b>{paymentRequest.code}</b> · {paymentRequest.requesterName || paymentRequest.requesterId}</p>
          </div>
          <button className="cvm-close" onClick={onClose} title="Đóng">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="cvm-body">
          {/* Info banner */}
          <div className="cvm-info-banner">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z" />
              <path d="M14 2v5h5" />
            </svg>
            <div>
              <p><b>Đang lập phiếu chi từ đề nghị {paymentRequest.code}</b></p>
              <p>{paymentRequest.reason} · <b>{formatVND(paymentRequest.amount)}</b> · {paymentRequest.requesterName}</p>
            </div>
          </div>

          {/* 2-col grid: Số PC, Hình thức, Ngày lập, Ngày chứng từ */}
          <div className="cvm-grid-2">
            <Field label="Số phiếu chi">
              <input value="Tự sinh khi lưu (PC-YYYYMM-XXXX · khác DNC-* của ĐNC)" disabled />
            </Field>
            <Field label="Hình thức chi">
              <select value={method} onChange={(e) => setMethod(e.target.value as VoucherMethod)}>
                <option value="tien_mat_vnd">{METHOD_LABEL.tien_mat_vnd}</option>
                <option value="chuyen_khoan">{METHOD_LABEL.chuyen_khoan}</option>
                <option value="tien_mat_usd">{METHOD_LABEL.tien_mat_usd}</option>
              </select>
            </Field>
            <Field label="Ngày lập phiếu">
              <input type="date" value={voucherDate} onChange={(e) => setVoucherDate(e.target.value)} />
            </Field>
            <Field label="Ngày chứng từ">
              <input type="date" value={documentDate} onChange={(e) => setDocumentDate(e.target.value)} />
            </Field>
          </div>

          {/* Bank source (visible when chuyển khoản) */}
          {isBank && (
            <div className="cvm-bank-box">
              <div className="cvm-bank-grid">
                <Field label="Tài khoản nguồn (NH)" tone="blue">
                  <select value={bankAccountUsed} onChange={(e) => setBankAccountUsed(e.target.value)}>
                    {bankOptions.length === 0 ? (
                      <option value="">— Chưa có TK NH · thêm ở Cài đặt →</option>
                    ) : (
                      companyBanks.map((b) => {
                        const label = formatBankOption(b);
                        return (
                          <option key={b.id} value={label}>
                            {label} · SD: {fmtShortVND(b.balance)}đ
                          </option>
                        );
                      })
                    )}
                  </select>
                </Field>
                <Field label="Số UNC / Biên nhận" tone="blue">
                  <input
                    value={uncNumber}
                    onChange={(e) => setUncNumber(e.target.value)}
                    placeholder="Auto sau khi ký"
                  />
                </Field>
              </div>

              {/* Balance panel */}
              {selectedBank && (
                <div className={`cvm-balance-panel ${isOverBalance ? 'over' : ''}`}>
                  <div className="cvm-balance-row">
                    <div>
                      <span className="cvm-balance-lbl">Số dư đầu kỳ</span>
                      <b>{formatVND(selectedBank.openingBalance)}</b>
                    </div>
                    <span className="cvm-balance-op">−</span>
                    <div>
                      <span className="cvm-balance-lbl">Đã chi</span>
                      <b>{formatVND(selectedBank.disbursed)}</b>
                    </div>
                    <span className="cvm-balance-op">=</span>
                    <div>
                      <span className="cvm-balance-lbl">Số dư khả dụng</span>
                      <b className={isOverBalance ? 'red' : 'green'}>{formatVND(selectedBank.balance)}</b>
                    </div>
                    {actualAmount > 0 && (
                      <>
                        <span className="cvm-balance-op">−</span>
                        <div>
                          <span className="cvm-balance-lbl">PC này</span>
                          <b className={isOverBalance ? 'red' : ''}>{formatVND(actualAmount)}</b>
                        </div>
                        <span className="cvm-balance-op">=</span>
                        <div>
                          <span className="cvm-balance-lbl">Sau khi chi</span>
                          <b className={isOverBalance ? 'red' : 'green'}>
                            {formatVND(selectedBank.balance - actualAmount)}
                          </b>
                        </div>
                      </>
                    )}
                  </div>
                  {isOverBalance && (
                    <div className="cvm-balance-warn">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" /><path d="M12 9v4M12 17h.01" /></svg>
                      <span>
                        <b>Vượt số dư khả dụng</b> {formatVND(actualAmount - selectedBank.balance)}.
                        Chọn TK khác hoặc giảm số tiền, hoặc nạp thêm tiền vào TK trước khi chi.
                      </span>
                    </div>
                  )}
                </div>
              )}

              <div className="cvm-bank-hint">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round">
                  <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
                  <path d="M12 9v4M12 17h.01" />
                </svg>
                <span>Bút toán sẽ ghi vào <b>Sổ tiền gửi NH</b> (TK 1121), không phải Sổ tiền mặt.</span>
              </div>
            </div>
          )}

          {method === 'tien_mat_vnd' && cashSummary && (
            <div className="cvm-bank-box cvm-cash-box">
              <div className={`cvm-balance-panel ${isOverBalance ? 'over' : ''}`}>
                <div className="cvm-balance-row">
                  <div><span className="cvm-balance-lbl">Số dư đầu kỳ</span><b>{formatVND(cashSummary.openingBalance)}</b></div>
                  <span className="cvm-balance-op">−</span>
                  <div><span className="cvm-balance-lbl">Đã chi</span><b>{formatVND(cashSummary.totalOut)}</b></div>
                  <span className="cvm-balance-op">=</span>
                  <div><span className="cvm-balance-lbl">Số dư khả dụng</span><b className={isOverBalance ? 'red' : 'green'}>{formatVND(cashSummary.closingBalance)}</b></div>
                  {actualAmount > 0 && <><span className="cvm-balance-op">−</span><div><span className="cvm-balance-lbl">PC này</span><b className={isOverBalance ? 'red' : ''}>{formatVND(actualAmount)}</b></div><span className="cvm-balance-op">=</span><div><span className="cvm-balance-lbl">Sau khi chi</span><b className={isOverBalance ? 'red' : 'green'}>{formatVND(cashSummary.closingBalance - actualAmount)}</b></div></>}
                </div>
              </div>
              <div className="cvm-bank-hint"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0L2.27 18A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4M12 17h.01"/></svg><span>Bút toán sẽ ghi vào <b>Sổ tiền mặt</b> (TK 1111).</span></div>
            </div>
          )}

          {/* Số tiền */}
          <Field label="Số tiền">
            <div className="cvm-amount-wrap">
              <input
                type="text"
                inputMode="numeric"
                value={amountInput}
                onChange={(e) => handleAmount(e.target.value)}
                placeholder="0"
              />
              <span>đ</span>
            </div>
          </Field>

          {/* Người nhận */}
          <div className="cvm-grid-2">
            <Field label="Loại người nhận">
              <select value={receiverType} onChange={(e) => setReceiverType(e.target.value as ReceiverType)}>
                <option value="employee">{RECEIVER_LABEL.employee}</option>
                <option value="supplier">{RECEIVER_LABEL.supplier}</option>
                <option value="customer">{RECEIVER_LABEL.customer}</option>
                <option value="other">{RECEIVER_LABEL.other}</option>
              </select>
            </Field>
            <Field label={paymentRequest.type === 'thuong_nong' ? 'Chọn người nhận thưởng' : 'Người nhận'}>
              {paymentRequest.type === 'thuong_nong' && paymentRequest.recipients?.length ? (
                <select value={bonusPick} onChange={(e) => handleBonusPick(e.target.value)}>
                  <option value="all">
                    Tất cả ({paymentRequest.recipients.length} NV · {formatVND(paymentRequest.amount)})
                  </option>
                  <optgroup label="Chi cho 1 NV cụ thể">
                    {paymentRequest.recipients.map((r) => (
                      <option key={r.employeeId} value={r.employeeId}>
                        {r.employeeName} · {formatVND(r.amount)}
                      </option>
                    ))}
                  </optgroup>
                  <option value="custom">✎ Nhập tên khác (đại diện nhận thay)</option>
                </select>
              ) : (
                <input
                  value={receiverName}
                  onChange={(e) => setReceiverName(e.target.value)}
                  placeholder="Tên đầy đủ"
                />
              )}
            </Field>
          </div>

          {/* Thưởng nóng: hiển thị tên người nhận đã resolve + cho phép edit khi custom */}
          {paymentRequest.type === 'thuong_nong' && paymentRequest.recipients?.length ? (
            <Field label={bonusPick === 'custom' ? 'Tên người đại diện nhận' : 'Tên trên phiếu chi'}>
              <input
                value={receiverName}
                onChange={(e) => setReceiverName(e.target.value)}
                disabled={bonusPick !== 'custom'}
                placeholder="Tên đầy đủ"
                style={bonusPick !== 'custom' ? { background: '#f8fafc', color: '#475569' } : undefined}
              />
            </Field>
          ) : null}

          <Field label="Số CMND/CCCD người nhận (nếu có)">
            <input
              value={receiverIdNumber}
              onChange={(e) => setReceiverIdNumber(e.target.value)}
              placeholder="0123456789"
            />
          </Field>

          <Field label="Diễn giải">
            <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>

          {/* Hạch toán — 2 dòng Nợ/Có, TK Nợ user chọn, TK Có auto theo Hình thức chi */}
          <div className="cvm-book">
            <div className="cvm-book-label">Hạch toán</div>

            {/* Dòng Nợ */}
            <div className="cvm-book-line">
              <span className="cvm-book-side cvm-book-debit">Nợ</span>
              <select value={debitAccount} onChange={(e) => setDebitAccount(e.target.value)} className="cvm-book-select">
                {debitAccounts.length === 0 ? (
                  <option value={debitAccount}>{debitAccount || '—'}</option>
                ) : (
                  <>
                    <optgroup label="Tài sản">
                      {debitAccounts.filter((a) => a.type === 'asset').map((a) => (
                        <option key={a.code} value={a.code}>{a.code} · {a.name}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Chi phí">
                      {debitAccounts.filter((a) => a.type === 'expense').map((a) => (
                        <option key={a.code} value={a.code}>{a.code} · {a.name}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Nợ phải trả">
                      {debitAccounts.filter((a) => a.type === 'liability').map((a) => (
                        <option key={a.code} value={a.code}>{a.code} · {a.name}</option>
                      ))}
                    </optgroup>
                  </>
                )}
              </select>
              <span className="cvm-book-amt">{formatVND(actualAmount)}</span>
            </div>

            {/* Dòng Có */}
            <div className="cvm-book-line">
              <span className="cvm-book-side cvm-book-credit">Có</span>
              <span className="cvm-book-static">
                <b>{creditAccount}</b> · {creditAccount === '1111' ? 'Tiền mặt VND' : creditAccount === '1121' ? 'Tiền gửi NH' : 'Tiền mặt USD'}
                <span className="cvm-book-static-hint">(theo hình thức chi)</span>
              </span>
              <span className="cvm-book-amt">{formatVND(actualAmount)}</span>
            </div>

            <div className="cvm-book-hint">
              💡 TK Nợ mặc định theo loại ĐNC ({paymentRequest.type === 'thuong_nong' ? 'Thưởng → 334' : paymentRequest.type === 'tam_ung' ? 'Tạm ứng → 141' : paymentRequest.type === 'hoan_ung' ? 'Hoàn ứng → 141' : 'Chi thường → 6421'}). Đổi được cho case khác (VD chi vật tư → 152, chi phí bán hàng → 641). TK Có theo Hình thức chi (1111/1121/1112) — đổi ở dropdown "Hình thức chi" phía trên.
            </div>
          </div>

          {error && <div className="pfm-error">⚠️ {error}</div>}
        </div>

        <div className="cvm-foot">
          <button className="pay-btn-outline" onClick={onClose} disabled={saving}>Huỷ</button>
          <button className="pay-btn-dark" onClick={handleSubmit} disabled={saving || isOverBalance}>
            {saving
              ? 'Đang lưu…'
              : isOverBalance
                ? 'Vượt số dư — không thể chi'
                : paymentRequest.status === 'PENDING_NVKT'
                  ? 'Lưu PC & chuyển KTT ký'
                  : 'Lưu PC & đánh dấu đã chi'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, tone }: { label: string; children: React.ReactNode; tone?: 'blue' }) {
  return (
    <div className={`cvm-field ${tone === 'blue' ? 'cvm-field-blue' : ''}`}>
      <label>{label}</label>
      {children}
    </div>
  );
}
